import { type CSSProperties, type KeyboardEvent, useEffect, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { EmployeeBasicDto } from "../../../../models/dtos/EmployeeBasic.dto";
import { useAppDispatch, useAppSelector } from "../../../../stores/Hooks";
import { performLogout } from "../../../../utils/performLogout";
import { employeeService } from "../../../hrm/services/EmployeeService";

export interface UserAccountMenuProps {
  triggerClassName?: string;
  triggerStyle?: CSSProperties;
}

interface UserAvatarProps {
  avatarUrl?: string;
  initials: string;
  className: string;
}

const getInitials = (fullName: string): string =>
  fullName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(-2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "U";

const UserAvatar = ({ avatarUrl, initials, className }: UserAvatarProps) => {
  if (avatarUrl) {
    return <img src={avatarUrl} alt="" className={`${className} rounded-full object-cover`} />;
  }

  return (
    <span className={`${className} flex shrink-0 items-center justify-center rounded-full bg-[#001e5a] font-semibold text-white`}>
      {initials}
    </span>
  );
};

export const UserAccountMenu = ({ triggerClassName = "", triggerStyle }: UserAccountMenuProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const menuId = useId();
  const { userId, username } = useAppSelector((state) => state.auth);
  const [employee, setEmployee] = useState<EmployeeBasicDto | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    let isActive = true;

    if (!userId) {
      setEmployee(null);
      return () => {
        isActive = false;
      };
    }

    employeeService
      .getEmployeeBasic(userId)
      .then((data) => {
        if (isActive) setEmployee(data);
      })
      .catch(() => {
        if (isActive) setEmployee(null);
      });

    return () => {
      isActive = false;
    };
  }, [userId]);

  const fullName = employee ? [employee.lastName, employee.firstName].filter(Boolean).join(" ") : (username ?? "User");
  const initials = getInitials(fullName);
  const avatarUrl = employee?.avatarId || undefined;

  const handleProfileClick = (): void => {
    setIsOpen(false);
    navigate("/hrm/employees/me");
  };

  const handleLogoutClick = async (): Promise<void> => {
    setIsOpen(false);
    await performLogout(dispatch);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") setIsOpen(false);
  };

  return (
    <div className="relative shrink-0 mr-1" onKeyDown={handleKeyDown}>
      <button
        type="button"
        aria-label="Mở menu tài khoản"
        aria-haspopup="menu"
        aria-controls={isOpen ? menuId : undefined}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
        style={triggerStyle}
        className={`flex shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 outline-none ${triggerClassName}`}
      >
        <UserAvatar avatarUrl={avatarUrl} initials={initials} className="h-6 w-6 shrink-0 text-[8px]" />
      </button>

      {isOpen && (
        <>
          <button
            type="button"
            tabIndex={-1}
            aria-label="Đóng menu tài khoản"
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-[999] cursor-default border-0 bg-transparent p-0"
          />

          <div
            id={menuId}
            role="menu"
            aria-label="Tài khoản người dùng"
            className="absolute right-0 top-[calc(100%+8px)] z-[1000] w-[216px] bg-white p-1 text-slate-800 shadow-[0_8px_20px_rgba(15,23,42,0.16)]"
          >
          <button
            type="button"
            role="menuitem"
            onClick={handleProfileClick}
            className="flex w-full items-center gap-2.5 border-0 border-l-2 border-l-transparent bg-white px-2.5 py-2 text-left transition-colors hover:border-l-[#001e5a] hover:bg-slate-50"
          >
            <UserAvatar avatarUrl={avatarUrl} initials={initials} className="h-[34px] w-[34px] shrink-0 text-[10px]" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12px] font-semibold leading-4 text-slate-900">{fullName}</span>
              <span className="block truncate text-[9px] font-medium uppercase tracking-[0.06em] text-slate-400">
                {employee?.employeeCode || "Chưa có mã NV"}
              </span>
            </span>
            <svg viewBox="0 0 20 20" aria-hidden="true" className="h-3 w-3 shrink-0 fill-none stroke-slate-400 stroke-[1.8]">
              <path d="m7.5 4.5 5 5.5-5 5.5" />
            </svg>
          </button>

          <div className="mx-1 h-px bg-slate-100" />

          <button
            type="button"
            role="menuitem"
            onClick={() => void handleLogoutClick()}
            className="flex w-full items-center gap-2.5 border-0 border-l-2 border-l-transparent bg-white px-2.5 py-2 text-left text-[11px] font-medium text-red-500 transition-colors hover:border-l-red-500 hover:bg-red-50 hover:text-red-600"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[14px] w-[34px] shrink-0 fill-none stroke-current stroke-[1.8]">
              <path d="M10 5H6.5A1.5 1.5 0 0 0 5 6.5v11A1.5 1.5 0 0 0 6.5 19H10M14.5 8l4 4-4 4M9 12h9" />
            </svg>
            <span>Đăng xuất</span>
          </button>
          </div>
        </>
      )}
    </div>
  );
};
